import { createAssetConversion } from 'Pods/ThreeDStudio/Services/conversions/createAssetConversion'
import { saveExportFormat } from 'Pods/ThreeDStudio/Services/exportFormatStorage'

import { threeDStudioStore } from '../index'
import { EXPORT_STATUS } from '../slices/export'

const PREPARING_TRANSITION_MS = 8000

let preparingTimer = null

const setExportState = (patch) => {
  threeDStudioStore.setState((state) => ({
    export: { ...state.export, ...patch },
  }))
}

const captureExportError = (err) => {
  if (window.Sentry) {
    window.Sentry.captureException(err, { extra: { source: 'ThreeDStudio.export' } })
  }
}

const clearPreparingTimer = () => {
  if (preparingTimer) {
    clearTimeout(preparingTimer)
    preparingTimer = null
  }
}

const schedulePreparingTransition = () => {
  clearPreparingTimer()
  preparingTimer = setTimeout(() => {
    const { status } = threeDStudioStore.getState().export
    if (status === EXPORT_STATUS.QUEUED) {
      setExportState({ status: EXPORT_STATUS.PREPARING })
    }
    preparingTimer = null
  }, PREPARING_TRANSITION_MS)
}

// Cross-origin presigned S3 URLs ignore the `download` attribute — the BE must
// set `response-content-disposition=attachment` on the presigned URL for the
// browser to save instead of opening inline.
const startBrowserDownload = (url) => {
  if (!url || typeof window === 'undefined') return

  const link = document.createElement('a')
  link.href = url
  link.rel = 'noopener noreferrer'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const failExport = (err) => {
  if (err) captureExportError(err)
  clearPreparingTimer()
  setExportState({ status: EXPORT_STATUS.ERROR })
}

const succeedExport = (url) => {
  clearPreparingTimer()
  setExportState({ status: EXPORT_STATUS.IDLE, activeConversion: null, downloadUrl: null })
  startBrowserDownload(url)
}

export const selectExportFormat = (format) => {
  setExportState({ selectedFormat: format })
  saveExportFormat(format)
}

// Re-queries the idempotent BE endpoint on hydrate so we snap to the real
// status (completed / pending) instead of pinning to QUEUED for 8s.
export const restoreActiveConversion = async (payload) => {
  if (!payload) return

  setExportState({
    status: EXPORT_STATUS.QUEUED,
    activeConversion: {
      conversionId: payload.conversion_id,
      sourceAssetId: payload.source_asset_id,
      runId: payload.run_id,
      ownerModelId: payload.run_id,
      format: payload.format,
    },
  })

  try {
    const response = await createAssetConversion(payload.source_asset_id, payload.format)
    if (response?.status === 'completed' && response.download_url) {
      succeedExport(response.download_url)
      return
    }
    schedulePreparingTransition()
  } catch (err) {
    failExport(err)
  }
}

export const dismissExportNotification = () => {
  clearPreparingTimer()
  setExportState({ status: EXPORT_STATUS.IDLE, activeConversion: null, downloadUrl: null })
}

export const handleConversionCompleted = (broadcastData = {}) => {
  const active = threeDStudioStore.getState().export.activeConversion
  if (!active) return

  if (broadcastData.download_url) {
    succeedExport(broadcastData.download_url)
    return
  }

  // BE contract guarantees a download_url on `completed`; treat its absence as
  // a failed broadcast so the user can retry.
  failExport(new Error('asset_conversion completed broadcast missing download_url'))
}

export const handleConversionFailed = () => {
  failExport()
}

export const triggerExport = async (assetId, format) => {
  if (!assetId || !format) return

  // We already hold the open model's run id, so seed it up front: the run-scoped
  // export notification can render immediately instead of waiting for the create
  // response to echo back the same run_id.
  const openedModelRunId = threeDStudioStore.getState().ui?.openedModel?.id
  setExportState({
    status: EXPORT_STATUS.QUEUED,
    activeConversion: { sourceAssetId: assetId, runId: openedModelRunId, ownerModelId: openedModelRunId, format },
    downloadUrl: null,
  })

  try {
    const response = await createAssetConversion(assetId, format)
    if (response?.status === 'completed' && response.download_url) {
      succeedExport(response.download_url)
      return
    }

    setExportState({
      activeConversion: {
        sourceAssetId: assetId,
        conversionId: response.conversion_id,
        runId: response.run_id,
        ownerModelId: openedModelRunId,
        format: response.format,
      },
    })
    schedulePreparingTransition()
  } catch (err) {
    failExport(err)
  }
}
